<script lang="ts" setup>
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import { useRoute } from 'vue-router'

  import { useDocument, useFirestore } from 'vuefire'

  import { collection, doc } from 'firebase/firestore'

  import useOuraAuth from '#web-oura-tools/auth'

  import { useIntroTour } from '#ourahub-intra/composables/introTour'

  import type { Service, ServiceDetailsSection } from '#ourahub-intra/types/services'

  const emits = defineEmits<{
    'show-info': [target?: { id?: string; section?: ServiceDetailsSection }]
  }>()

  const route = useRoute()

  const ouraAuth = useOuraAuth()

  const firestore = useFirestore()

  const { startTour } = useIntroTour()

  const pathname = ref(window.location.pathname)

  const onPopState = () => (pathname.value = window.location.pathname)

  onMounted(() => window.addEventListener('popstate', onPopState))
  onBeforeUnmount(() => window.removeEventListener('popstate', onPopState))

  const documentationHistory = ref<{ id: string; app: string; url: string; title: string; icon: string }[]>([])

  const ouraHubHelp = {
    id: 'outo-ourahub-intra',
    url: 'https://ouraring.atlassian.net/wiki/spaces/SW/pages/429768468/OuraHub+Portal',
    title: 'What is OuraHub portal?',
    icon: 'mdi-open-in-new',
    app: 'OuraHub',
  }

  const versionsHelp = {
    sandbox: { color: 'green-lighten-2', title: 'Development version', icon: 'mdi-dev-to' },
    staging: { color: 'purple-lighten-2', title: 'Experimental version', icon: 'mdi-flask-outline' },
    release: { color: '', title: 'Production version', icon: 'mdi-rocket-launch-outline' },
  }

  const { data: service } = useDocument<Service>(
    () => (route.name ? doc(collection(firestore, 'service-registry'), route.name as string) : null),
    { reset: true }, // reset: true clears the document data if route.name becomes undefined, i.e. on main page
  )

  const serviceHelp = computed(() => {
    const versionIcon = versionsHelp[ouraAuth.appEnv.value]?.icon || ouraHubHelp.icon
    const versionTitle = versionsHelp[ouraAuth.appEnv.value]?.title || ouraHubHelp.title

    return service.value?.docs?.findLast((d) => pathname.value.startsWith(d.route)) || route.name === 'home'
      ? null
      : {
          id: service.value?.id || '',
          app: service.value?.name || '',
          title: versionTitle,
          icon: versionIcon,
          url: '',
        }
  })

  watch(
    serviceHelp,
    (newHelp) => {
      if (newHelp) {
        documentationHistory.value = [
          {
            id: service.value?.id || '',
            app: service.value?.name || '',
            url: newHelp.url,
            title: newHelp.title,
            icon: service.value?.icon || 'mdi-text-box-multiple-outline',
          },
          ...documentationHistory.value.filter((h) => h.url !== newHelp.url),
        ].slice(0, 5)
      }
    },
    { immediate: true },
  )

  function open(
    help: { id: string; app: string; url: string; title: string; icon: string },
    section?: ServiceDetailsSection,
  ) {
    if (!help.url) {
      emits('show-info', { id: help.id, section })
    } else {
      window.open(help.url, '_blank', 'noopener,noreferrer')
    }
  }
</script>

<template language="html">
  <v-expand-x-transition mode="out-in" name="documentation-popup">
    <v-chip
      rounded
      color="white"
      target="_blank"
      class="text-white my-n1 px-4"
      data-intro-tour-id="current-doc"
      :prepend-icon="serviceHelp?.icon || ouraHubHelp.icon"
      @click="open(serviceHelp || ouraHubHelp, 'versions')"
    >
      {{ serviceHelp?.title || ouraHubHelp.title }}
    </v-chip>
  </v-expand-x-transition>

  <v-menu offset="8" width="300" location="bottom end">
    <template #activator="{ props }">
      <o-btn-text
        v-bind="props"
        data-intro-tour-id="help"
        icon="mdi-help-circle-outline"
        :color="versionsHelp[ouraAuth.appEnv.value]?.color || ''"
      />
    </template>

    <v-list class="bg-primary pb-4">
      <v-list-subheader>OuraHub information</v-list-subheader>

      <v-list-item title="Show intro tour" prepend-icon="mdi-compass-outline" @click="startTour()" />

      <v-list-item
        target="_blank"
        title="OuraHub portal info"
        prepend-icon="mdi-text-box-multiple-outline"
        :href="ouraHubHelp.url"
      />

      <v-list-item
        v-if="route.name !== 'home'"
        target="_blank"
        title="Current app / tool info"
        prepend-icon="mdi-information-outline"
        @click="emits('show-info')"
      />

      <v-divider />

      <v-list-subheader>Documentation for recent views</v-list-subheader>

      <v-list-item
        v-if="documentationHistory.length === 0"
        disabled
        prepend-icon="mdi-history"
        title="No recent documentation"
      />

      <v-list-item
        v-for="page in documentationHistory"
        :key="`${page.app} ${page.url}`"
        :title="page.title"
        :subtitle="page.app"
        :prepend-icon="page.icon"
        @click="open(page)"
      />
    </v-list>
  </v-menu>
</template>
